import type { TProfile } from "@shared/entities";
import type { TRequest } from "@shared/entities";
import type { TDocumentVariableValues } from "@shared/entities";
import { dosageTypeOptionsLabels } from "@shared/entities";

/**
 * Build the variable values map from a client profile and request.
 * Used both for client-side preview substitution and as the
 * canonical source when filling a template before editing.
 *
 * All values are strings. Missing/undefined fields produce "".
 * dob is formatted as DD/MM/YYYY (RTL-safe Israeli format).
 * currentDate is the current date in the same format.
 */
export function profileToVariableValues(
  profile: TProfile,
  request: TRequest
): TDocumentVariableValues {
  return {
    clientName: formatName(profile.firstName, profile.lastName),
    clientIdNumber: profile.IDNumber ?? "",
    clientDateOfBirth: formatDate(profile.dob),
    clientPhone: profile.phone ?? "",
    currentDate: formatDate(Date.now()),
    currentDosage: formatDosageList(request.existsDosage),
    requestedDosage: formatDosageList(request.dosages),
  };
}

/**
 * Build the variable values map from a request's embedded clientDetails.
 * Use this when a TProfile is not separately available (e.g. admin/doctor views
 * where only the TRequest is loaded).
 */
export function requestToVariableValues(request: TRequest): TDocumentVariableValues {
  const cd = request.clientDetails;
  return {
    clientName: formatName(cd.firstName, cd.lastName),
    clientIdNumber: cd.IDNumber ?? "",
    clientDateOfBirth: formatDate(cd.dob),
    clientPhone: cd.phone ?? "",
    currentDate: formatDate(Date.now()),
    currentDosage: formatDosageList(request.existsDosage),
    requestedDosage: formatDosageList(request.dosages),
  };
}

function formatDosageList(dosages: TRequest["dosages"] | undefined): string {
  if (!dosages || dosages.length === 0) return "";
  return dosages
    .map((d) => {
      const typeLabel = dosageTypeOptionsLabels[d.type as keyof typeof dosageTypeOptionsLabels] ?? d.type;
      return `${typeLabel} ${d.amount} גרם (${d.centralism ?? ""})`.trim();
    })
    .join(" + ");
}

function formatName(first?: string, last?: string): string {
  return [first, last].filter(Boolean).join(" ").trim();
}

/**
 * Format a timestamp (ms) or date string to DD/MM/YYYY.
 * Returns "" for falsy/invalid values.
 */
export function formatDate(value?: string | number | null): string {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}
